import React, { useEffect } from "react";
import { Form, Input, Modal } from "antd";
import { Folder } from "../../redux/interfaces/folder.types";
import { validateEmail } from "../../utils/vault/generalValidators.ts";

// Define field names type
type FieldName =
  | "email"
  | "optional_email"
  | "name"
  | "first_surname"
  | "second_surname"
  | "phone_number"
  | "rfc"
  | "curp";

interface ParticipantFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: any) => Promise<void>;
  participantType: "advisor" | "tenant" | "guarantor" | "landlord";
  initialData?: any;
  folder: Folder;
  isEdit: boolean;
}

// Type the field labels mapping
const fieldLabels: Record<FieldName, string> = {
  email: "Correo electrónico",
  optional_email: "Correo electrónico alternativo",
  name: "Nombre",
  first_surname: "Apellido paterno",
  second_surname: "Apellido materno",
  phone_number: "Teléfono",
  rfc: "RFC",
  curp: "CURP",
};

// Type the participant fields mapping
const participantFields: Record<
  ParticipantFormModalProps["participantType"],
  FieldName[]
> = {
  advisor: [
    "email",
    "optional_email",
    "name",
    "first_surname",
    "second_surname",
    "phone_number",
  ],
  tenant: ["email", "name", "first_surname", "second_surname", "phone_number"],
  guarantor: [
    "email",
    "name",
    "first_surname",
    "second_surname",
    "phone_number",
  ],
  landlord: [
    "email",
    "name",
    "first_surname",
    "second_surname",
    "phone_number",
  ],
};

const isParticipantFieldRequired = (
  participantType: ParticipantFormModalProps["participantType"],
  field: FieldName,
): boolean => {
  switch (participantType) {
    case "advisor":
    case "tenant":
      return field !== "optional_email" && field !== "second_surname";
    case "landlord":
      return field === "email";
    case "guarantor":
      return false;
  }
};

export const ParticipantFormModal: React.FC<ParticipantFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  participantType,
  initialData,
  isEdit,
}) => {
  const [form] = Form.useForm();

  const title = `${isEdit ? "Editar" : "Agregar"} ${
    participantType === "advisor"
      ? "Asesor"
      : participantType === "tenant"
        ? "Inquilino"
        : participantType === "guarantor"
          ? "Fiador"
          : "Propietario"
  }`;

  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
    }
  }, [isOpen, form]);

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      if (initialData) {
        form.setFieldsValue(initialData);
      }
    }
  }, [isOpen, initialData, form, participantType]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit({
        metadata: {
          [participantType]: values,
        },
      });
      onClose();
    } catch (error) {
      console.error("Validation error:", error);
    }
  };

  return (
    <Modal
      title={title}
      open={isOpen}
      onCancel={onClose}
      onOk={handleSubmit}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {participantFields[participantType].map((field: FieldName) => (
          <Form.Item
            key={field}
            name={field}
            label={fieldLabels[field]}
            rules={[
              {
                required: isParticipantFieldRequired(participantType, field),
                message: `Por favor ingrese ${fieldLabels[field].toLowerCase()}`,
              },
              ...(field === "email" || field === "optional_email"
                ? [
                    {
                      validator: validateEmail,
                    },
                  ]
                : []),
            ]}
          >
            <Input />
          </Form.Item>
        ))}
      </Form>
    </Modal>
  );
};
